import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  Warning,
  ArrowClockwise,
  FloppyDisk,
  Gauge,
  Stack,
  Info,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_redirect_rate_limit: '',
    max_links_free_plan: '',
  });

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const config = await adminApi.getConfig();
      setForm({
        default_redirect_rate_limit: config.default_redirect_rate_limit || '',
        max_links_free_plan: config.max_links_free_plan ?? '',
      });
    } catch (err) {
      console.error('Failed to load config', err);
      setError(true);
      toast.error('Failed to load platform configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await adminApi.updateConfig({
        default_redirect_rate_limit: form.default_redirect_rate_limit,
        max_links_free_plan: Number(form.max_links_free_plan),
      });
      toast.success('Configuration saved successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-3xl mx-auto w-full">
        <div className="h-9 w-40 bg-muted rounded-xl animate-pulse mb-2" />
        <div className="h-4 w-56 bg-muted rounded-lg animate-pulse mb-6" />
        <div className="surface-card p-6">
          <div className="space-y-6">
            <div>
              <div className="h-4 w-48 bg-muted rounded-md animate-pulse mb-2" />
              <div className="h-10 w-full bg-muted rounded-xl animate-pulse" />
            </div>
            <div>
              <div className="h-4 w-40 bg-muted rounded-md animate-pulse mb-2" />
              <div className="h-10 w-full bg-muted rounded-xl animate-pulse" />
            </div>
            <div className="h-10 w-32 bg-muted rounded-xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-10 animate-fade-in" data-testid="admin-settings-error">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load configuration
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch the platform configuration. Check your connection and try again.
          </p>
          <Button
            onClick={fetchConfig}
            data-testid="retry-btn"
            className="rounded-xl gradient-primary text-white hover:opacity-95 px-6 py-2.5 font-semibold gap-2"
          >
            <ArrowClockwise size={16} weight="bold" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-3xl mx-auto w-full" data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-[2.5rem] font-extrabold tracking-tight text-foreground leading-tight">
          Platform settings
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Tune the global limits and policies that apply to every workspace.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="surface-card p-6 md:p-8 ambient-shadow space-y-6"
      >
        <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 flex items-start gap-3">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary/15 text-primary shrink-0">
            <Info size={14} weight="fill" />
          </span>
          <p className="text-xs text-foreground/80 leading-relaxed">
            Changes apply globally and take effect immediately for new redirects and link
            creations. Existing links and rate limit windows are not retroactively modified.
          </p>
        </div>

        <div>
          <Label
            htmlFor="default_redirect_rate_limit"
            className="text-sm font-semibold text-foreground flex items-center gap-2"
          >
            <Gauge size={14} className="text-primary" weight="fill" />
            Default redirect rate limit
          </Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Format: <span className="font-mono">number/window</span> — e.g.
            <span className="font-mono ml-1">1000/minute</span>,
            <span className="font-mono ml-1">200/hour</span>.
          </p>
          <input
            id="default_redirect_rate_limit"
            type="text"
            value={form.default_redirect_rate_limit}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                default_redirect_rate_limit: e.target.value,
              }))
            }
            className="input-modern font-mono"
            placeholder="e.g. 1000/minute"
            data-testid="rate-limit-input"
          />
        </div>

        <div>
          <Label
            htmlFor="max_links_free_plan"
            className="text-sm font-semibold text-foreground flex items-center gap-2"
          >
            <Stack size={14} className="text-primary" weight="fill" />
            Max links per free workspace
          </Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Maximum number of active short links a free-tier workspace may create.
          </p>
          <input
            id="max_links_free_plan"
            type="number"
            min={1}
            value={form.max_links_free_plan}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, max_links_free_plan: e.target.value }))
            }
            className="input-modern"
            placeholder="e.g. 50"
            data-testid="max-links-input"
          />
        </div>

        <div className="flex justify-end pt-2 border-t border-border/70">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-xl gradient-primary text-white hover:opacity-95 px-6 h-11 font-semibold gap-2 ambient-shadow"
            data-testid="save-btn"
          >
            <FloppyDisk size={16} weight="bold" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
